import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { useMemo } from "react";
import { useWSQueries } from "@ws-react-query";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { usePayerInsuranceCoverageConfig } from "./configs/payer/usePayerInsuranceCoverageConfig";
import { usePayeeInsuranceCoverageConfig } from "./configs/payee/usePayeeInsuranceCoverageConfig";
import { buildConfigFields } from "../../utils";
import { getInsuranceCoverageRequestFromCache } from "./configs/helpers/getInsuranceCoverageRequestFromCache";
import { getInsuranceCoverageMonitor } from "../../../../services/api/insurance/monitor/getInsuranceCoverageMonitor";
import { QUERY_INSURANCE_COVERAGE_MONITOR } from "../../../../query/insuranceCoverageMonitor/queries/useInsuranceCoverageMonitorQuery";
import { InsuranceCoverageMonitor } from "../../../../services/api/insurance/monitor/types";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    QUERY_INSURANCE_COVERAGE_MONITOR,
    requirements,
    getInsuranceCoverageMonitor,
    RequirementType.InsuranceCoverage
  );

  return useWSQueries({ queries });
};

export const useInsuranceCoverageRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.InsuranceCoverage] => {
  const contextQueries = useContextQueries(requirements);
  const payerInsuranceCoverageConfig =
    usePayerInsuranceCoverageConfig(callback);
  const payeeInsuranceCoverageConfig =
    usePayeeInsuranceCoverageConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<
      InsuranceCoverageMonitor,
      RequirementType.InsuranceCoverage
    >({
      payerConfig: payerInsuranceCoverageConfig,
      payeeConfig: payeeInsuranceCoverageConfig,
      getAdditionalData: getInsuranceCoverageRequestFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getInsuranceCoverageRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(
      query => !query.isFetched || query.isLoading
    ),
    dataUpdatedAt: queriesHash
  };
};
